import { grpc } from '@improbable-eng/grpc-web'
import { toStringValue } from '~/grpc/grpc-util'
import { NodeHttpTransport } from '@improbable-eng/grpc-web-node-http-transport'
import { commonMetaData } from '~/store/_helpers/common-helper'

const toString = value => {
  return typeof value === 'undefined' ? value : String(value)
}
const toNumber = value => {
  if (typeof value === 'undefined') {
    return undefined
  }
  if (isNaN(value)) {
    throw { grpcCode: grpc.Code.InvalidArgument }
  }
  return Number(value)
}

// initial state
export const state = () => ({
  serviceDetail: {
    id: null,
    providerId: null,
    parentCategoryId: null,
    parentCategoryName: '',
    childCategoryId: null,
    childCategoryName: '',
    formatType: '',
    priceWot: null,
    unitTime: null,
    name: '',
    overview: '',
    catchphrase: '',
    head: '',
    body: '',
    deliveryTime: null,
    orderLimit: null,
    opened: null,
    openedDate: null,
    manualStoppedDate: null,
    serviceClass: null,
    stopFg: false,
    anonymityAllowFg: false,
    instantOnlyFg: false,
    negotiateFg: false,
    videoType: null,
    proFlag: false,
    customizeFlag: false,
    customizeNotice: '',
    shipmentFlag: false,
    moviePriorityFlag: false,
    fullCorrespondenceFlag: false,
    created: null,
    modified: null,
    favCount: null,
    performanceCount: null,
    serviceMoviesList: [],
    serviceImagesList: [],
    rating: {
      indicator: null,
      ratingCount: null
    },
    imageCount: null,
    orderStockCount: null,
    openedTalkroomCount: null,
    optionsList: [
      {
        id: null,
        title: '',
        priceWot: null
      }
    ],
    faqsList: [
      {
        question: '',
        answer: ''
      }
    ],
    sampleMessagesList: [
      {
        userMessage: '',
        providerMessage: ''
      }
    ]
  },
  provisionFormat: {},
  serviceStyle: {},
  specifications: [],
  buyer: {
    isFavorited: false,
    coupon: {
      isExpiring: false,
      discount: null,
      discountType: null,
      expireDate: null,
      displayName: '',
      description: ''
    }
  },
  isNotActive: false,
  pointRatio: null,
  purchaseStatus: '',
  providerPhoneStatus: ''
})

// getters
export const getters = {}

// actions
export const actions = {
  async serviceRequests({ commit, rootState }, { params, fullPath, sprKey }) {
    const [{ GetServiceDetailRequest }, { ServiceService }] = await Promise.all([
      import('~/stub/apigateway/service/service_pb'),
      import('~/stub/apigateway/service/service_pb_service')
    ])
    const getRequest = new GetServiceDetailRequest()
    const setMetadata = commonMetaData(rootState)
    setMetadata['x-full-path'] = [fullPath]
    getRequest.setServiceId(toNumber(params.id))
    if (sprKey) {
      getRequest.setSprKey(toStringValue(sprKey))
    }

    const serviceServiceReply = await new Promise((resolve, reject) => {
      grpc.invoke(ServiceService.GetServiceDetail, {
        request: getRequest,
        metadata: setMetadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, message, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })

    commit('RECEIVE_SERVICE_REQUESTS', {
      search: serviceServiceReply
    })
  },
  async providerServicesRequests({ commit, rootState }, data) {
    const [{ GetServicesRequest }, { ServiceService }] = await Promise.all([
      import('~/stub/apigateway/profile/service_pb'),
      import('~/stub/apigateway/profile/service_pb_service')
    ])
    const getProviderServicesRequest = new GetServicesRequest()
    const setMetadata = commonMetaData(rootState)
    const toNumber = value => {
      return typeof value === 'undefined' ? value : Number(value)
    }
    getProviderServicesRequest.setUserId(toNumber(data.userId))
    getProviderServicesRequest.setExcludeServiceId(toNumber(data.serviceId))
    getProviderServicesRequest.setUserAction('view_service_recommend_provider')

    const getProviderServicesReply = await new Promise((resolve, reject) => {
      grpc.invoke(ServiceService.GetServices, {
        request: getProviderServicesRequest,
        metadata: setMetadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, message, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    return getProviderServicesReply
  },
  async recommendRequests({ commit, rootState }, data) {
    const [{ RecommendServicesRequest }, { SearchService }] = await Promise.all([
      import('~/stub/apigateway/service/search_pb'),
      import('~/stub/apigateway/service/search_pb_service')
    ])
    const getRecommendRequest = new RecommendServicesRequest()
    const setMetadata = commonMetaData(rootState)
    getRecommendRequest.setServiceId(toNumber(data.serviceId))
    getRecommendRequest.setAlgorithm(data.algorithm)
    getRecommendRequest.setBusinessFlag(data.businessFlag)

    const getRecommendReply = await new Promise((resolve, reject) => {
      grpc.invoke(SearchService.RecommendServices, {
        request: getRecommendRequest,
        metadata: setMetadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, message, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    return getRecommendReply
  },
  async addServiceReservation({ commit, rootState }, serviceId) {
    const [{ AddServiceReservationRequest }, { ServiceService }] = await Promise.all([
      import('~/stub/apigateway/service/service_pb'),
      import('~/stub/apigateway/service/service_pb_service')
    ])
    const addServiceReservationRequest = new AddServiceReservationRequest()
    const setMetadata = commonMetaData(rootState)
    addServiceReservationRequest.setServiceId(toNumber(serviceId))

    const AddServiceReservationReply = await new Promise((resolve, reject) => {
      grpc.invoke(ServiceService.AddServiceReservation, {
        request: addServiceReservationRequest,
        metadata: setMetadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, message, trailers) => {
          const decodedMessage = this.$util.getGrpcErrorMessage(trailers, process.client)
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode, message: decodedMessage })
          }
        }
      })
    })
    return AddServiceReservationReply
  }
}

// mutations
export const mutations = {
  RECEIVE_SERVICE_REQUESTS(state, data) {
    Object.assign(state, data.search)
  }
}
